import { useCallback, useSyncExternalStore } from 'react';

import { fg } from '@atlaskit/platform-feature-flags';
import { QueryIntent } from '@atlassian/search-ai/constants';
import { type IntentClassificationData } from '@atlassian/search-ai/query-intent-api/types';
import { isNavContentIntent, isNavigationalIntent } from '@atlassian/search-ai/utils';

interface SmartAnswersIntentStore {
	workspaceId: string | null;
	intentClassificationData: IntentClassificationData | null | undefined;
	sainJql: string | null;
	queryIntent: QueryIntent;
	/** True once `useQueryIntent` has finished classifying the current query. */
	isIntentClassified: boolean;
	/** True when SAIN will render an AI answer (not just a bookmark/popular suggestion/nothing). */
	isSainAnswerVisible: boolean;
}

const NAV_BOOST_SCORE_THRESHOLD = 0.5;

const initialState: SmartAnswersIntentStore = {
	workspaceId: null,
	intentClassificationData: null,
	sainJql: null,
	queryIntent: QueryIntent.DEFAULT,
	isIntentClassified: false,
	isSainAnswerVisible: false,
};

// Singleton state shared across all components
const sharedState: { current: SmartAnswersIntentStore } = {
	current: { ...initialState },
};

// Pub/sub listeners for reactive SAIN JQL updates
type Listener = () => void;
const sainJqlListeners = new Set<Listener>();

function notifySainJqlListeners(): void {
	sainJqlListeners.forEach((l) => l());
}

function subscribeSainJql(listener: Listener): () => void {
	sainJqlListeners.add(listener);
	return () => {
		sainJqlListeners.delete(listener);
	};
}

function getSainJqlSnapshot(): string | null {
	return sharedState.current.sainJql;
}

// Pub/sub listeners for reactive queryIntent updates
const queryIntentListeners = new Set<Listener>();

function notifyQueryIntentListeners(): void {
	queryIntentListeners.forEach((l) => l());
}

function subscribeQueryIntent(listener: Listener): () => void {
	queryIntentListeners.add(listener);
	return () => {
		queryIntentListeners.delete(listener);
	};
}

function getQueryIntentSnapshot(): QueryIntent {
	return sharedState.current.queryIntent;
}

// Pub/sub listeners for reactive isIntentClassified updates
const isIntentClassifiedListeners = new Set<Listener>();

function notifyIsIntentClassifiedListeners(): void {
	isIntentClassifiedListeners.forEach((l) => l());
}

function subscribeIsIntentClassified(listener: Listener): () => void {
	isIntentClassifiedListeners.add(listener);
	return () => {
		isIntentClassifiedListeners.delete(listener);
	};
}

function getIsIntentClassifiedSnapshot(): boolean {
	return sharedState.current.isIntentClassified;
}

// Pub/sub listeners for reactive isSainAnswerVisible updates
const isSainAnswerVisibleListeners = new Set<Listener>();

function notifyIsSainAnswerVisibleListeners(): void {
	isSainAnswerVisibleListeners.forEach((l) => l());
}

function subscribeIsSainAnswerVisible(listener: Listener): () => void {
	isSainAnswerVisibleListeners.add(listener);
	return () => {
		isSainAnswerVisibleListeners.delete(listener);
	};
}

function getIsSainAnswerVisibleSnapshot(): boolean {
	return sharedState.current.isSainAnswerVisible;
}

/**
 * Reactive hook that subscribes to queryIntent changes in the shared store.
 * Allows multiple components to read the same queryIntent without each
 * calling useQueryIntent() independently (which would trigger duplicate
 * API calls and analytics events).
 */
export function useSharedQueryIntent(): QueryIntent {
	return useSyncExternalStore(
		subscribeQueryIntent,
		getQueryIntentSnapshot,
		getQueryIntentSnapshot,
	);
}

/** Reactive hook reading `isIntentClassified` from the shared store. */
export function useSharedIsIntentClassified(): boolean {
	return useSyncExternalStore(
		subscribeIsIntentClassified,
		getIsIntentClassifiedSnapshot,
		getIsIntentClassifiedSnapshot,
	);
}

/** Reactive hook reading `isSainAnswerVisible` from the shared store. */
export function useSharedIsSainAnswerVisible(): boolean {
	return useSyncExternalStore(
		subscribeIsSainAnswerVisible,
		getIsSainAnswerVisibleSnapshot,
		getIsSainAnswerVisibleSnapshot,
	);
}

/**
 * Reactive hook that subscribes to SAIN JQL changes.
 * Unlike getSainJql() which is read-once, this triggers re-renders
 * whenever setSainJql() is called, making it safe to use in href computation.
 */
export function useSainJql(): string | null {
	return useSyncExternalStore(subscribeSainJql, getSainJqlSnapshot, getSainJqlSnapshot);
}

/**
 * React hook for managing smart answers intent store
 * Provides state management for smart answers high confidence result cards
 * Uses a shared singleton state to ensure all components see the same results
 */
export const useSmartAnswersIntentStore: () => {
	setWorkspaceId: (workspaceId: string) => void;
	getWorkspaceId: () => string | null;
	setQueryIntentClassificationData: (intentClassificationData: IntentClassificationData) => void;
	isSAINNavigationalExperienceEligible: ({
		position,
		url,
		navBoostScore,
	}: {
		position: number;
		url: string;
		navBoostScore: number | null | undefined;
	}) => boolean;
	clearStore: () => void;
	setSainJql: (jql: string | null) => void;
	getSainJql: () => string | null;
	setQueryIntent: (queryIntent: QueryIntent) => void;
	getQueryIntent: () => QueryIntent;
	setIsIntentClassified: (isIntentClassified: boolean) => void;
	setIsSainAnswerVisible: (isSainAnswerVisible: boolean) => void;
} = () => {
	const state = sharedState;

	const setWorkspaceId = useCallback(
		(workspaceId: string): void => {
			state.current.workspaceId = workspaceId;
		},
		[state],
	);

	const getWorkspaceId = useCallback((): string | null => {
		return state.current.workspaceId;
	}, [state]);

	const setQueryIntentClassificationData = useCallback(
		(intentClassificationData: IntentClassificationData): void => {
			state.current.intentClassificationData = intentClassificationData;
		},
		[state],
	);

	const clearStore = useCallback((): void => {
		state.current = { ...initialState };
		notifySainJqlListeners();
		notifyQueryIntentListeners();
		notifyIsIntentClassifiedListeners();
		notifyIsSainAnswerVisibleListeners();
	}, [state]);

	const setQueryIntent = useCallback(
		(queryIntent: QueryIntent): void => {
			if (state.current.queryIntent === queryIntent) {
				return;
			}
			state.current.queryIntent = queryIntent;
			notifyQueryIntentListeners();
		},
		[state],
	);

	const setIsIntentClassified = useCallback(
		(isIntentClassified: boolean): void => {
			if (state.current.isIntentClassified === isIntentClassified) {
				return;
			}
			state.current.isIntentClassified = isIntentClassified;
			notifyIsIntentClassifiedListeners();
		},
		[state],
	);

	const setIsSainAnswerVisible = useCallback(
		(isSainAnswerVisible: boolean): void => {
			if (state.current.isSainAnswerVisible === isSainAnswerVisible) {
				return;
			}
			state.current.isSainAnswerVisible = isSainAnswerVisible;
			notifyIsSainAnswerVisibleListeners();
		},
		[state],
	);

	const getQueryIntent = useCallback((): QueryIntent => {
		return state.current.queryIntent;
	}, [state]);

	const setSainJql = useCallback(
		(jql: string | null): void => {
			state.current.sainJql = jql;
			notifySainJqlListeners();
		},
		[state],
	);

	const getSainJql = useCallback((): string | null => {
		return state.current.sainJql;
	}, [state]);

	const isSAINNavigationalExperienceEligible = useCallback(
		({
			position,
			url,
			navBoostScore,
		}: {
			position: number;
			url: string;
			navBoostScore: number | null | undefined;
		}): boolean => {
			if (position === 0 && fg('kd_enable_sain_hero_nav')) {
				const queryIntentClassificationData = state.current.intentClassificationData;

				// use the URL check for nav content intent
				if (queryIntentClassificationData && isNavContentIntent(queryIntentClassificationData)) {
					const navContentUrl = queryIntentClassificationData.url;
					if (navContentUrl === url) {
						return true;
					}
				}

				// use the nav boost score check for navigational intent or nav content intent if we missed the document
				// because the URL returned is not for the first result
				if (
					queryIntentClassificationData &&
					(isNavigationalIntent(queryIntentClassificationData) ||
						isNavContentIntent(queryIntentClassificationData))
				) {
					if (navBoostScore && navBoostScore > NAV_BOOST_SCORE_THRESHOLD) {
						return true;
					}
				}
			}

			return false;
		},
		[state],
	);

	return {
		setWorkspaceId,
		getWorkspaceId,
		setQueryIntentClassificationData,
		isSAINNavigationalExperienceEligible,
		clearStore,
		setSainJql,
		getSainJql,
		setQueryIntent,
		getQueryIntent,
		setIsIntentClassified,
		setIsSainAnswerVisible,
	};
};
